import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import { useAuth } from '@/hooks/useAuth';
import PhotoLightbox, { type LightboxPhoto } from '@/components/feature/PhotoLightbox';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import FileUploader from '@/components/feature/FileUploader';
import { DAILY_LOG_SYNC_EVENT } from '@/lib/dailyLogUtils';
import { useSignedFileUrls } from '@/lib/storageSignedUrl';

interface DBPhoto {
  id: string;
  project_id: string;
  file_name: string;
  file_url: string;
  storage_bucket?: string | null;
  storage_path?: string | null;
  file_size: number | null;
  file_type: string | null;
  note: string | null;
  uploaded_by: string | null;
  created_at: string;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

import { formatDate as formatDateLocale } from '@/lib/dateUtils';

function formatDate(iso: string): string {
  return formatDateLocale(iso);
}

interface PhotosTabProps {
  projectId: string;
  canUpload?: boolean;
}

export default function PhotosTab({ projectId, canUpload = true }: PhotosTabProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<DBPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState<LightboxPhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState('');

  // Signed display URLs keyed by photo id (falls back to file_url per record).
  const photoUrls = useSignedFileUrls(photos);

  useEffect(() => {
    if (projectId) loadPhotos();
  }, [projectId]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (projectId && detail?.projectId && detail.projectId !== projectId) return;
      loadPhotos();
    };
    window.addEventListener(DAILY_LOG_SYNC_EVENT, handler);
    return () => window.removeEventListener(DAILY_LOG_SYNC_EVENT, handler);
  }, [projectId]);

  async function loadPhotos() {
    setLoading(true);
    const { data } = await supabase
      .from('jphph_project_files')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });

    if (data) {
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      const imageFiles = (data as DBPhoto[]).filter((f) => {
        const ext = f.file_name.split('.').pop()?.toLowerCase() || '';
        return imageExts.includes(ext) || f.file_type === 'image';
      });
      setPhotos(imageFiles);
    }
    setLoading(false);
  }

  async function handleUploadComplete(records: Array<{ id: string; file_name: string; file_url: string; file_type: string | null; file_size: string | number | null }>) {
    if (user) {
      await createNotification({
        project_id: projectId,
        notification_type: 'photo',
        title: `${records.length} new photo${records.length > 1 ? 's' : ''} uploaded`,
        body: t('photos.newPhotosAdded'),
        target_role: 'management',
        link_path: `/management/projects/${projectId}`,
        created_by: user.id,
      });
      await createNotification({
        project_id: projectId,
        notification_type: 'photo',
        title: `${records.length} new photo${records.length > 1 ? 's' : ''} uploaded`,
        body: t('photos.newPhotosAddedClient'),
        target_role: 'client',
        link_path: `/client/updates`,
        created_by: user.id,
      });
    }
    setShowUploadModal(false);
    setUploadError('');
    await loadPhotos();
  }

  async function handleDelete(photoId: string) {
    setDeletingId(photoId);
    try {
      // Find the photo to get the URL
      const photo = photos.find((p) => p.id === photoId);
      if (!photo) throw new Error(t('photos.photoNotFound'));

      // Delete from database first
      const { error: dbError } = await supabase
        .from('jphph_project_files')
        .delete()
        .eq('id', photoId);

      if (dbError) throw dbError;

      // Try to delete from storage (best effort — ignore errors).
      // Prefer the explicit storage reference; fall back to parsing file_url.
      try {
        if (photo.storage_bucket && photo.storage_path) {
          await supabase.storage.from(photo.storage_bucket).remove([photo.storage_path]);
        } else {
          const url = new URL(photo.file_url);
          const pathParts = url.pathname.split('/');
          const bucket = 'project-files';
          const path = pathParts.slice(pathParts.indexOf(bucket) + 1).join('/');
          if (path) {
            await supabase.storage.from(bucket).remove([path]);
          }
        }
      } catch {
        // Storage path parse errors — ignore
      }

      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      setShowDeleteConfirm(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('photos.deleteFailed');
      setUploadError(msg);
    } finally {
      setDeletingId(null);
    }
  }

  const openLightbox = (index: number) => {
    const lbPhotos: LightboxPhoto[] = photos.map((p) => ({
      url: photoUrls[p.id] || p.file_url,
      caption: p.note ? `${p.file_name} — ${p.note}` : p.file_name,
    }));
    setLightboxPhotos(lbPhotos);
    setLightboxIndex(index);

    // Debug log: confirm what URL we're using
    if (photos[index]) {
      const displayUrl = photoUrls[photos[index].id] || photos[index].file_url;
      const img = new Image();
      img.onload = () => {
        // eslint-disable-next-line no-console
        console.log('[PhotosTab] lightbox image loaded:', {
          src: displayUrl,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
        });
      };
      img.src = displayUrl;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-[#E8621A]">
            <i className="ri-loader-4-line text-3xl animate-spin"></i>
          </div>
          <p className="text-[#9CA3AF] text-sm">{t('photos.loadingPhotos')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Lightbox */}
      {lightboxIndex !== null && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => { setLightboxIndex(null); setLightboxPhotos([]); }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(null)}></div>
          <div className="relative bg-white w-full max-w-sm mx-4 rounded-3xl p-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-[#FEF2F2] rounded-2xl">
              <i className="ri-delete-bin-line text-[#EF4444] text-2xl"></i>
            </div>
            <p className="text-[#1A1A1A] font-bold text-base mb-1">{t('photos.deletePhoto')}</p>
            <p className="text-[#6B7280] text-sm mb-5">{t('photos.cannotBeUndone')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                {t('photos.cancel')}
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deletingId === showDeleteConfirm}
                className="flex-1 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {deletingId === showDeleteConfirm ? t('photos.deleting') : t('photos.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#1A1A1A] text-sm">{t('photos.title')}</h3>
        {canUpload && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-2-line"></i>{t('photos.upload')}
          </button>
        )}
      </div>

      {/* Empty state */}
      {photos.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-image-line text-4xl"></i>
          </div>
          <p className="text-[#374151] font-semibold text-sm mb-1">{t('photos.noPhotos')}</p>
          <p className="text-[#9CA3AF] text-xs">{t('photos.uploadToDocument')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-colors"
            >
              {/* Photo wrapper — overflow-hidden + rounded only here, NOT on outer card */}
              <div
                className="relative w-full bg-[#f8fafc] overflow-hidden rounded-t-2xl"
                style={{ height: 'clamp(200px, 28vw, 260px)' }}
              >
                <button
                  onClick={() => openLightbox(index)}
                  className="relative w-full h-full cursor-pointer block"
                >
                  <ProjectPhotoImage
                    src={photoUrls[photo.id] || photo.file_url}
                    alt={photo.file_name}
                    sizes="(max-width: 640px) 50vw, 33vw"
                    loading="lazy"
                    debugContext="PhotosTab"
                    className="w-full h-full object-cover object-top"
                  />
                </button>

                {/* Delete button — top-right corner */}
                {canUpload && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(photo.id);
                    }}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-lg bg-white/90 text-[#6B7280] hover:text-[#EF4444] hover:bg-white cursor-pointer shadow-sm z-10 opacity-0 group-hover:opacity-100"
                    title={t('photos.delete')}
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>

              {/* Info below the image */}
              <div className="px-3 py-2">
                <div className="min-w-0">
                  <p className="text-[#374151] text-xs font-medium truncate">{photo.file_name}</p>
                  {photo.note ? (
                    <p className="text-[#E8621A] text-[11px] mt-0.5 line-clamp-2 leading-snug">
                      <i className="ri-sticky-note-line mr-0.5"></i>{photo.note}
                    </p>
                  ) : null}
                  <p className="text-[#9CA3AF] text-[10px] mt-0.5">{formatDate(photo.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && canUpload && user && (
        <FileUploader
          projectId={projectId}
          currentUserId={user.id}
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={handleUploadComplete}
          accountType="management"
          category="photo"
          allowMultiple={true}
          allowCamera={true}
          enablePerFileCaption={true}
          title={t('photos.uploadPhotos')}
        />
      )}
    </div>
  );
}